// Utility to get today's opening hours as a string (e.g. '09:00 - 17:00' or 'Closed')
export function getTodaysOpeningHours(openingHours?: OpeningHours[], now: Date = new Date()): string {
  if (!openingHours || openingHours.length === 0) return "Closed";
  const currentDay = now.toLocaleDateString("en-US", { weekday: "long" }) as OpeningHours["dayOfWeek"];
  const today = openingHours.find(h => h.dayOfWeek === currentDay);
  if (!today || today.isClosed) return "Closed";
  return `${today.openTime} - ${today.closeTime}`;
}
// Returns filtered, non-expired shops
export function filterVisibleShops(shops: Shop[], now: Date = new Date()): Shop[] {
  return shops.filter((shop) => !isShopExpired(shop, now));
}

// Returns shops matching selected tags (case-insensitive)
export function filterShopsByTags(shops: Shop[], selectedFilters: string[]): Shop[] {
  if (!selectedFilters.length) return shops;
  return shops.filter((shop) =>
    shop.tags && selectedFilters.some((filter) =>
      shop.tags!.some((tag) => tag.toLowerCase() === filter.toLowerCase())
    )
  );
}

import { Shop, OpeningHours } from "../types/Shop";

// ShopStatus type and getShopStatus moved from MapMarker.ts for decoupling
export type ShopStatus = "open" | "closingSoon" | "closed";
export const getShopStatus = (openingHours?: OpeningHours[]): { status: ShopStatus } => {
  if (!openingHours?.length) {
    return { status: "closed" };
  }
  const now = new Date();
  const currentDay = now.toLocaleDateString("en-US", { weekday: "long" }) as OpeningHours["dayOfWeek"];
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const todayHours = openingHours.find((h) => h.dayOfWeek === currentDay);
  if (!todayHours || todayHours.isClosed || !todayHours.openTime || !todayHours.closeTime) {
    return { status: "closed" };
  }
  const openMinutes = parseInt(todayHours.openTime.split(":")[0]) * 60 + parseInt(todayHours.openTime.split(":")[1]);
  const closeMinutes = parseInt(todayHours.closeTime.split(":")[0]) * 60 + parseInt(todayHours.closeTime.split(":")[1]);
  const isCurrentlyOpen = currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
  if (isCurrentlyOpen) {
    const minutesUntilClose = closeMinutes - currentMinutes;
    if (minutesUntilClose <= 60) {
      return { status: "closingSoon" };
    } else {
      return { status: "open" };
    }
  } else {
    return { status: "closed" };
  }
};

// Returns shop open/close status, status text, and time info for today
export const getShopStatusInfo = (shop: Shop) => {
  const { status } = getShopStatus(shop.openingHours);
  const currentlyOpen = status === "open" || status === "closingSoon";
  const statusText = status === "open" ? "Open" : status === "closingSoon" ? "Closing Soon" : "Closed";

  let timeInfo = "";
  const hours = shop.openingHours;
  if (hours && hours.length > 0) {
    const now = new Date();
    const currentDay = now.toLocaleDateString("en-US", { weekday: "long" }) as OpeningHours["dayOfWeek"];
    const today: OpeningHours | undefined = hours.find((h: OpeningHours) => h.dayOfWeek === currentDay);
    if (today && today.openTime && today.closeTime) {
      const [oh, om] = today.openTime.split(":").map(Number);
      const [ch, cm] = today.closeTime.split(":").map(Number);
      const openMinutes = oh * 60 + om;
      const closeMinutes = ch * 60 + cm;
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      if (currentlyOpen) {
        const m = closeMinutes - nowMinutes;
        timeInfo = m > 60 ? `Closes in ${Math.floor(m/60)}h ${m%60}m` : `Closes in ${m}m`;
      } else if (nowMinutes < openMinutes) {
        const m = openMinutes - nowMinutes;
        timeInfo = m > 60 ? `Opens in ${Math.floor(m/60)}h ${m%60}m` : `Opens in ${m}m`;
      } else {
        timeInfo = `Opens tomorrow at ${today.openTime}`;
      }
    }
  }
  return { currentlyOpen, statusText, timeInfo };
};


export const isShopExpired = (shop: Shop, now: Date = new Date()): boolean => {
  if (!shop.isCustom || !shop.expiresAt) return false;
  const expiresAt = new Date(shop.expiresAt);
  return expiresAt <= now;
};

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatTimeRemaining = (expiresAt: string, now: Date = new Date()): string => {
  const expires = new Date(expiresAt);
  const timeLeft = expires.getTime() - now.getTime();
  if (timeLeft <= 0) return 'Expired';
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

export const formatOpeningHours = (hours: OpeningHours[]): string[] => {
  return hours.map(h => {
    if (h.isClosed) {
      return `${h.dayOfWeek}: Closed`;
    }
    return `${h.dayOfWeek}: ${h.openTime} - ${h.closeTime}`;
  });
};

export const getExpirationInfo = (shop: Shop, now: Date = new Date()) => {
  if (!shop.isCustom || !shop.expiresAt) return null;
  const expiresAt = new Date(shop.expiresAt);
  const timeLeft = expiresAt.getTime() - now.getTime();
  if (timeLeft <= 0) {
    return { expired: true, text: "Expired" };
  } else {
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const timeText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    return { expired: false, text: `Expires in ${timeText}` };
  }
};
